/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import {
  Home, ClipboardList, MessageCircle, Users, User, Settings, BookOpen, ShieldCheck, HeartHandshake,
  Sparkles, Lightbulb, Bookmark, Calendar, Stethoscope, Activity, Package, Bandage, Wrench, Bot, Mic,
  FileArchive, GraduationCap, Receipt, FileSignature, LayoutDashboard, DatabaseBackup, LockKeyhole,
  ShieldAlert, Mail, Gauge,
} from 'lucide-react';

/**
 * Navigation configuration for the AppShell (sidebar + bottom nav).
 * Extracted from AppShell.jsx (Code Quality refactor).
 */

export const BOTTOM_NAV_ITEMS = [
  { key: 'feed', path: '/feed', labelKey: 'feed', Icon: Home },
  { key: 'bacheca', path: '/bacheca', labelKey: 'bacheca', Icon: ClipboardList },
  { key: 'chat', path: '/chat', labelKey: 'chat', Icon: MessageCircle },
  { key: 'directory', path: '/directory', labelKey: 'directory', Icon: Users },
];

export const PATIENT_BOTTOM_NAV_ITEMS = [
  { key: 'telemedicine', path: '/telemedicine', labelKey: 'telemedNavLabel', Icon: Stethoscope },
  { key: 'ai-patient', path: '/ai-patient', labelKey: 'aiPatientNavLabel', Icon: Sparkles },
  { key: 'chat', path: '/chat', labelKey: 'chat', Icon: MessageCircle },
  { key: 'profile', path: '/profile', labelKey: 'profile', Icon: User },
];

export const SIDEBAR_ITEMS = [
  { key: 'feed', path: '/feed', labelKey: 'feed', Icon: Home },
  { key: 'bacheca', path: '/bacheca', labelKey: 'bacheca', Icon: ClipboardList },
  { key: 'telemedicine', path: '/telemedicine', labelKey: 'telemedNavLabel', Icon: Stethoscope },
  { key: 'my-services', path: '/my-services', labelKey: 'myServicesNav', Icon: Package },
  { key: 'pro-toolkit', path: '/pro-toolkit', labelKey: 'proToolkitNav', Icon: Wrench },
  { key: 'voice-diary', path: '/voice-diary', labelKey: 'voiceDiaryNav', Icon: Mic },
  { key: 'fse', path: '/fse', labelKey: 'fseNav', Icon: FileArchive },
  { key: 'ecm', path: '/ecm', labelKey: 'ecmNav', Icon: GraduationCap },
  { key: 'wound-care', path: '/wound-care', labelKey: 'woundCareNav', Icon: Bandage },
  { key: 'care-team', path: '/care-team', labelKey: 'careTeamNav', Icon: Users },
  { key: 'ai', path: '/ai', labelKey: 'aiAssistant', Icon: Sparkles },
  { key: 'resources', path: '/resources', labelKey: 'resources', Icon: BookOpen },
  { key: 'wellness', path: '/wellness', labelKey: 'wellness', Icon: HeartHandshake },
  { key: 'chat', path: '/chat', labelKey: 'chat', Icon: MessageCircle },
  { key: 'directory', path: '/directory', labelKey: 'directory', Icon: Users },
  { key: 'saved', path: '/saved', labelKey: 'savedTitle', Icon: Bookmark },
  { key: 'shifts', path: '/shifts', labelKey: 'shiftsTitle', Icon: Calendar },
  { key: 'invoicing', path: '/invoicing', labelKey: 'invoicingNav', Icon: Receipt },
  { key: 'consents', path: '/consents', labelKey: 'consentsNav', Icon: FileSignature },
  { key: 'referti', path: '/referti', labelKey: 'refertiNav', Icon: LockKeyhole },
  { key: 'feedback', path: '/feedback', labelKey: 'feedbackTitle', Icon: Lightbulb },
  { key: 'profile', path: '/profile', labelKey: 'profile', Icon: User },
];

export const PATIENT_SIDEBAR_ITEMS = [
  { key: 'telemedicine', path: '/telemedicine', labelKey: 'telemedNavLabel', Icon: Stethoscope },
  { key: 'ai-patient', path: '/ai-patient', labelKey: 'aiPatientNavLabel', Icon: Sparkles },
  { key: 'agentic-health', path: '/agentic-health', labelKey: 'agenticHealthNav', Icon: Bot },
  { key: 'portale', path: '/portale', labelKey: 'portaleNav', Icon: LockKeyhole },
  { key: 'health-data', path: '/health-data', labelKey: 'healthDataNav', Icon: Activity },
  { key: 'wound-care', path: '/wound-care', labelKey: 'woundCareNav', Icon: Bandage },
  { key: 'care-team', path: '/care-team', labelKey: 'careTeamNav', Icon: Users },
  { key: 'fse', path: '/fse', labelKey: 'fseNav', Icon: FileArchive },
  { key: 'chat', path: '/chat', labelKey: 'chat', Icon: MessageCircle },
  { key: 'health-apps', path: '/resources', labelKey: 'patientHealthAppsNav', Icon: BookOpen },
  { key: 'feedback', path: '/feedback', labelKey: 'feedbackTitle', Icon: Lightbulb },
  { key: 'profile', path: '/profile', labelKey: 'profile', Icon: User },
];

export const ADMIN_SIDEBAR_ITEMS = [
  { key: 'admin', path: '/admin', labelKey: 'admin', Icon: ShieldCheck },
  { key: 'direzione', path: '/admin/direzione', labelKey: 'direzioneNav', Icon: LayoutDashboard },
  { key: 'backup', path: '/admin/backup', labelKey: 'backupNav', Icon: DatabaseBackup },
  { key: 'validation', path: '/admin/validation', labelKey: 'validationNav', Icon: ShieldAlert },
  { key: 'email', path: '/admin/email', labelKey: 'emailNav', Icon: Mail },
  { key: 'aiUsage', path: '/admin/ai-usage', labelKey: 'aiUsageNav', Icon: Gauge },
];

export const SETTINGS_SIDEBAR_ITEM = { key: 'settings', path: '/settings', labelKey: 'settings', Icon: Settings };

/** True for patient accounts (role or legacy category). */
export const isPatientUser = (user) => user?.role === 'patient' || user?.category === 'paziente';

/** Active-route rule shared by sidebar and bottom nav. */
export const isNavActive = (pathname, path) => pathname === path || (path !== '/feed' && pathname.startsWith(path));

export const getBottomNavItems = (user) => (isPatientUser(user) ? PATIENT_BOTTOM_NAV_ITEMS : BOTTOM_NAV_ITEMS);

export const getSidebarItems = (user) => [
  ...(isPatientUser(user) ? PATIENT_SIDEBAR_ITEMS : SIDEBAR_ITEMS),
  ...(user?.is_admin ? ADMIN_SIDEBAR_ITEMS : []),
  SETTINGS_SIDEBAR_ITEM,
];
